import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export interface PlayerTrack {
	id: string;
	url: string;
	title: string;
}

interface AudioPlayerContextType {
	currentTrack: PlayerTrack | null;
	isError: boolean;
	isPlaying: boolean;
	playTrack: (track: PlayerTrack) => void;
	toggleTrack: () => void;
	pause: () => void;
	duration: number;
	currentTime: number;
	isCurrentTrack: (track: PlayerTrack) => boolean;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const AudioPlayerProvider = ({ children }: { children: ReactNode }) => {
	const { t } = useLingui();
	const [currentTrack, setCurrentTrack] = useState<PlayerTrack | null>(null);
	const [isError, setIsError] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);

	const audioRef = useRef<HTMLAudioElement | null>(null);

	useEffect(() => {
		audioRef.current = new Audio();
	}, []);

	const playTrack = (track: PlayerTrack) => {
		if (!audioRef.current) {
			return;
		}

		setIsError(false);

		if (currentTrack?.id !== track.id) {
			audioRef.current.src = track.url;
			audioRef.current.play();
			setCurrentTrack(track);
			setIsPlaying(true);
		} else {
			toggleTrack();
		}
	};

	const toggleTrack = () => {
		if (!audioRef.current) {
			return;
		}

		if (audioRef.current.paused) {
			if (isError) {
				audioRef.current.load();
			}
			audioRef.current.play();
			setIsPlaying(true);
		} else {
			audioRef.current.pause();
			setIsPlaying(false);
		}
	};

	const pause = () => {
		if (!audioRef.current) {
			return;
		}

		audioRef.current.pause();
		setIsPlaying(false);
	};

	const isCurrentTrack = (track: PlayerTrack) => currentTrack?.id === track.id;

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		const onLoadedMetadata = () => setDuration(audio.duration);
		const onTimeUpdate = () => setCurrentTime(audio.currentTime);
		const onEnded = () => setIsPlaying(false);
		const onError = () => {
			setIsError(true);
			audio.pause();
			setIsPlaying(false);

			if (!audio.error) {
				toast.error(t`This media file could not be played for an unknown reason.`);
				return;
			}

			switch (audio.error.code) {
				case MediaError.MEDIA_ERR_ABORTED:
					toast.error(t`Audio playback aborted by user.`);
					break;
				case MediaError.MEDIA_ERR_NETWORK:
					toast.error(t`Network error while fetching audio.`);
					break;
				case MediaError.MEDIA_ERR_DECODE:
					toast.error(t`Decoding error: corrupted audio file.`);
					break;
				case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
					toast.error(t`Audio format not supported or invalid URL.`);
					break;
				default:
					toast.error(t`Unknown error occurred during audio playback.`);
					break;
			}
		};

		audio.addEventListener("loadedmetadata", onLoadedMetadata);
		audio.addEventListener("timeupdate", onTimeUpdate);
		audio.addEventListener("ended", onEnded);
		audio.addEventListener("error", onError);

		return () => {
			audio.removeEventListener("loadedmetadata", onLoadedMetadata);
			audio.removeEventListener("timeupdate", onTimeUpdate);
			audio.removeEventListener("ended", onEnded);
			audio.removeEventListener("error", onError);
		};
	}, []);

	return (
		<AudioPlayerContext.Provider
			value={{
				currentTrack,
				isError,
				isPlaying,
				playTrack,
				toggleTrack,
				pause,
				duration,
				currentTime,
				isCurrentTrack,
			}}
		>
			{children}
		</AudioPlayerContext.Provider>
	);
};

export const useAudioPlayer = () => {
	const ctx = useContext(AudioPlayerContext);

	if (!ctx) throw new Error("useAudioPlayer must be used within AudioPlayerProvider");

	return ctx;
};
